import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add, Flash, ShoppingCart, TickCircle } from "iconsax-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ProductIconTile } from "@/components/Atoms";
import { CopyLinkRow, QuantityStepper, SearchField, SefanoButton } from "@/components/Molecules";
import { formatPersianNumber } from "@/lib/format/persian";
import type { QuickOrderHeroProps } from "./QuickOrderHero.types";
import * as styles from "./QuickOrderHero.styles";

export type { QuickOrderHeroProps } from "./QuickOrderHero.types";

export const QuickOrderHero = memo(function QuickOrderHero({
  searchQuery,
  onSearchChange,
  filteredProducts,
  cartItems,
  totalPrice,
  orderLink,
  orderError,
  isProductsLoading = false,
  isCreatingOrder = false,
  onAddProduct,
  onQuantityChange,
  onCreateOrderLink,
  onReset,
}: QuickOrderHeroProps & { onReset?: () => void }) {
  const { t } = useTranslation("home");
  const theme = useTheme();

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(event.target.value);
    },
    [onSearchChange],
  );

  const visibleProducts = useMemo(() => {
    const list = searchQuery.trim() ? filteredProducts : filteredProducts;
    return list.slice(0, 6);
  }, [filteredProducts, searchQuery]);

  const cartQtyMap = useMemo(
    () => new Map(cartItems.map((item) => [item.product.id, item.quantity])),
    [cartItems],
  );

  const cartEmpty = cartItems.length === 0;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.heroHeader}>
        <Box sx={styles.heroIconWrap}>
          <Flash size={21} variant="Bold" color="#fff" />
        </Box>
        <Box>
          <Typography sx={styles.heroTitle}>{t("dashboard.quickOrder.heroTitle")}</Typography>
          <Typography sx={styles.heroSubtitle}>{t("dashboard.quickOrder.heroSubtitle")}</Typography>
        </Box>
      </Box>

      <Box sx={styles.innerCard}>
        <Box sx={styles.pickerColumn}>
          <SearchField
            placeholder={t("dashboard.quickOrder.searchPlaceholder")}
            value={searchQuery}
            onChange={handleSearchChange}
            fieldSize="compact"
          />

          {isProductsLoading ? (
            <Box sx={styles.loadingWrap}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={styles.productGrid}>
              {visibleProducts.map((product) => {
                const qty = cartQtyMap.get(product.id) ?? 0;
                const inCart = qty > 0;

                return (
                  <Box
                    key={product.id}
                    sx={styles.productTile(inCart)}
                    onClick={() => onAddProduct(product)}
                    role="button"
                    tabIndex={0}
                  >
                    <ProductIconTile imageUrl={product.image_url} alt={product.name} />
                    <Box sx={styles.productInfo}>
                      <Typography sx={styles.productName}>{product.name}</Typography>
                      <Typography sx={styles.productPrice}>
                        {formatPersianNumber(product.price)} ریال
                      </Typography>
                    </Box>
                    {inCart ? (
                      <Box sx={styles.qtyBadge}>{formatPersianNumber(qty)}</Box>
                    ) : (
                      <Box component="span" sx={styles.addButton}>
                        <Add size={18} variant="Linear" color={theme.palette.primary.main} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={styles.cartColumn(theme)}>
          {cartEmpty ? (
            <Box sx={styles.emptyCart(theme)}>
              <ShoppingCart size={34} variant="Linear" color={theme.palette.text.disabled} />
              <Typography variant="body2" color="text.disabled" sx={{ lineHeight: 1.7, fontSize: "0.78125rem" }}>
                {t("dashboard.quickOrder.emptyCartHint")}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={styles.cartList}>
                {cartItems.map((item) => (
                  <Box key={item.product.id} sx={styles.cartItem}>
                    <Box sx={styles.cartItemInfo}>
                      <Typography sx={styles.cartItemName}>{item.product.name}</Typography>
                      <Typography sx={styles.cartItemPrice}>
                        {formatPersianNumber(item.product.price * item.quantity)} ریال
                      </Typography>
                    </Box>
                    <QuantityStepper
                      value={item.quantity}
                      onChange={(qty) => onQuantityChange(item.product.id, qty)}
                    />
                  </Box>
                ))}
              </Box>

              <Box sx={styles.totalRow(theme)}>
                <Typography sx={styles.totalLabel}>{t("dashboard.quickOrder.totalLabel")}</Typography>
                <Typography sx={styles.totalValue}>
                  {formatPersianNumber(totalPrice)}{" "}
                  <Typography component="span" sx={styles.totalCurrency}>
                    ریال
                  </Typography>
                </Typography>
              </Box>
            </>
          )}

          {!orderLink && orderError ? (
            <Typography role="alert" sx={styles.ctaError}>
              {orderError}
            </Typography>
          ) : null}

          {!orderLink ? (
            <SefanoButton
              intent="primary"
              fullWidth
              sx={styles.ctaButton}
              disabled={cartEmpty || isCreatingOrder}
              onClick={onCreateOrderLink}
            >
              {isCreatingOrder ? t("dashboard.quickOrder.creating") : t("dashboard.quickOrder.createLink")}
            </SefanoButton>
          ) : (
            <Box sx={styles.linkReady}>
              <Typography sx={styles.linkReadyLabel}>
                <TickCircle size={17} variant="Bold" color={theme.palette.success.main} />
                {t("dashboard.quickOrder.linkReady")}
              </Typography>
              <CopyLinkRow url={orderLink} size="sm" copyLabel={t("dashboard.quickOrder.copyLink")} />
              {onReset ? (
                <Box component="button" type="button" sx={styles.resetLink} onClick={onReset}>
                  {t("dashboard.quickOrder.resetOrder")}
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});
